import React, { Suspense, useEffect } from "react";
import {
  Stack,
  Grid,
  Group,
  Title,
  Text,
  Paper,
  TextInput,
  Textarea,
  TagsInput,
  Switch,
  Checkbox,
  Button,
  ActionIcon,
  Loader,
  Image as MantineImage,
  Box,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  type FileWithPath,
} from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { MAX_UPLOAD_SIZE, rejectionMessage } from "../../../utils/uploads";
import { modals } from "@mantine/modals";
import {
  IconArrowLeft,
  IconUpload,
  IconPhoto,
  IconTrash,
  IconEye,
  IconX,
} from "@tabler/icons-react";
import { router, usePage } from "@inertiajs/react";
import BrandVehicleSelect, {
  type BrandOption,
  type VehicleOption,
} from "./components/BrandVehicleSelect";
import type { AdminArticleForm } from "./types";

// CRITIQUE (Pitfall 2 / UI-SPEC) : chargement LAZY de l'editeur Tiptap.
// Pas d'import statique -> le bundle SSR (resolve non-eager) ne le tire jamais ;
// ProseMirror ne s'evalue que cote client, sous <Suspense>.
const TiptapEditor = React.lazy(() => import("./components/TiptapEditor"));

const ImageField = ({
  label,
  preview,
  onDrop,
  onRemove,
}: {
  label: string;
  preview: string | null;
  onDrop: (file: FileWithPath) => void;
  onRemove: () => void;
}) => (
  <Stack gap="xs">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    {preview && (
      <Box style={{ position: "relative" }}>
        <MantineImage
          src={preview}
          alt={label}
          radius="md"
          h={140}
          fit="contain"
        />
        <ActionIcon
          size="sm"
          color="dark"
          variant="filled"
          style={{ position: "absolute", top: 6, right: 6 }}
          onClick={onRemove}
        >
          <IconX size={12} />
        </ActionIcon>
      </Box>
    )}
    <Dropzone
      accept={IMAGE_MIME_TYPE}
      maxSize={MAX_UPLOAD_SIZE}
      multiple={false}
      onDrop={(files) => onDrop(files[0])}
      // Sans onReject, un fichier hors limites etait ecarte en silence :
      // rien ne bougeait dans l'interface et la cause restait invisible.
      onReject={(rejections) =>
        notifications.show({
          color: "red",
          title: label,
          message: rejectionMessage(rejections),
        })
      }
    >
      <Group
        justify="center"
        gap="md"
        mih={80}
        style={{ pointerEvents: "none" }}
      >
        <Dropzone.Accept>
          <IconUpload size={32} />
        </Dropzone.Accept>
        <Dropzone.Idle>
          <IconPhoto size={32} />
        </Dropzone.Idle>
        <Text size="sm" c="dimmed">
          Glissez une image ou cliquez pour sélectionner
        </Text>
      </Group>
    </Dropzone>
  </Stack>
);

type ArticleFormProps = {
  article: AdminArticleForm | null;
  available_brands: BrandOption[];
  available_vehicles: VehicleOption[];
};

type FormValues = {
  title: string;
  slug_override: string;
  body: string;
  meta_title: string;
  meta_description: string;
  tags: string[];
  brand_ids: number[];
  vehicle_ids: number[];
  status: "draft" | "published";
  featured_image: FileWithPath | null;
  cover_image: FileWithPath | null;
  use_card_as_cover: boolean;
  remove_featured_image: boolean;
  remove_cover_image: boolean;
};

const AdminArticlesForm: React.FC<ArticleFormProps> = ({
  article,
  available_brands,
  available_vehicles,
}) => {
  const { props } = usePage<{ errors?: Record<string, string> }>();
  const isEdit = Boolean(article);

  const form = useForm<FormValues>({
    initialValues: {
      title: article?.title ?? "",
      slug_override: article?.slug_override ?? "",
      body: article?.body ?? "",
      meta_title: article?.meta_title ?? "",
      meta_description: article?.meta_description ?? "",
      tags: article?.tags ?? [],
      brand_ids: article?.brand_ids ?? [],
      vehicle_ids: article?.vehicle_ids ?? [],
      status: article?.status ?? "draft",
      featured_image: null,
      cover_image: null,
      use_card_as_cover:
        !article?.cover_image_url ||
        article?.cover_image_url === article?.featured_image_url,
      remove_featured_image: false,
      remove_cover_image: false,
    },
    validate: {
      title: (v) => (v.trim().length === 0 ? "Le titre est requis." : null),
    },
  });

  // Mappe les erreurs Inertia (errors:) sur les champs (rouge sous le champ) — UI-SPEC.
  useEffect(() => {
    if (props.errors && Object.keys(props.errors).length > 0) {
      form.setErrors(props.errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.errors]);

  // Slug auto-genere (placeholder), pre-rempli depuis le titre — D-14.
  const autoSlug = form.values.title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  const buildFormData = (values: FormValues) => {
    const fd = new FormData();
    fd.append("article[title]", values.title);
    fd.append("article[slug_override]", values.slug_override);
    fd.append("article[body]", values.body);
    fd.append("article[meta_title]", values.meta_title);
    fd.append("article[meta_description]", values.meta_description);
    fd.append("article[status]", values.status);
    // Toujours envoyer la cle (meme vide) pour permettre de tout deselectionner.
    if (values.tags.length === 0) fd.append("article[tags][]", "");
    values.tags.forEach((t) => fd.append("article[tags][]", t));
    if (values.brand_ids.length === 0) fd.append("article[brand_ids][]", "");
    values.brand_ids.forEach((id) =>
      fd.append("article[brand_ids][]", String(id)),
    );
    if (values.vehicle_ids.length === 0)
      fd.append("article[vehicle_ids][]", "");
    values.vehicle_ids.forEach((id) =>
      fd.append("article[vehicle_ids][]", String(id)),
    );
    if (values.remove_featured_image) {
      fd.append("article[remove_featured_image]", "1");
    } else if (values.featured_image) {
      fd.append("article[featured_image]", values.featured_image);
    }
    if (values.use_card_as_cover) {
      fd.append("article[use_featured_as_cover]", "1");
    } else if (values.remove_cover_image) {
      fd.append("article[remove_cover_image]", "1");
    } else if (values.cover_image) {
      fd.append("article[cover_image]", values.cover_image);
    }
    return fd;
  };

  const handleSubmit = (values: FormValues) => {
    const data = buildFormData(values);
    const onSuccess = () =>
      notifications.show({
        color: "green",
        title: "Article",
        message: "Article enregistré.",
      });
    const onError = () =>
      notifications.show({
        color: "red",
        title: "Article",
        message:
          "L'article n'a pas pu être enregistré. Vérifiez les champs en rouge.",
      });

    if (isEdit && article) {
      // Inertia : on force PATCH via _method pour un envoi multipart.
      data.append("_method", "patch");
      router.post(`/admin/articles/${article.slug}`, data, {
        onSuccess,
        onError,
      });
    } else {
      router.post("/admin/articles", data, { onSuccess, onError });
    }
  };

  const handleDelete = () => {
    if (!article) return;
    modals.openConfirmModal({
      title: "Supprimer cet article ?",
      children: (
        <Text size="sm">
          Cette action est définitive. L'article et son contenu seront retirés
          du site.
        </Text>
      ),
      labels: { confirm: "Supprimer l'article", cancel: "Conserver l'article" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        router.delete(`/admin/articles/${article.slug}`, {
          onSuccess: () =>
            notifications.show({
              color: "green",
              title: "Article",
              message: "Article supprimé.",
            }),
        }),
    });
  };

  const handlePreview = () => {
    if (!article) return;
    // Preview admin (D-17) : la page article rend meme les brouillons pour un admin.
    window.open(`/blog/articles/${article.slug}`, "_blank", "noopener");
  };

  const featuredPreview = form.values.remove_featured_image
    ? null
    : form.values.featured_image
      ? URL.createObjectURL(form.values.featured_image)
      : (article?.featured_image_url ?? null);

  const coverPreview = form.values.remove_cover_image
    ? null
    : form.values.cover_image
      ? URL.createObjectURL(form.values.cover_image)
      : (article?.cover_image_url ?? null);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        <Group gap={6} align="center">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => router.visit("/admin/articles")}
          >
            <IconArrowLeft size={15} />
          </ActionIcon>
          <Text size="sm" c="dimmed">
            Articles
          </Text>
          <Text size="sm" c="dimmed">
            /
          </Text>
          <Title order={2}>
            {isEdit ? "Éditer l'article" : "Nouvel article"}
          </Title>
        </Group>

        <Grid gap="xl">
          {/* Colonne principale (~70%) */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="xl">
              {/* Contenu — sans cadre ni titre de section */}
              <Stack gap="md">
                <TextInput
                  label="Titre"
                  withAsterisk
                  {...form.getInputProps("title")}
                />
                <TextInput
                  label="Slug"
                  description="Pré-rempli depuis le titre, modifiable."
                  placeholder={autoSlug || "slug-de-l-article"}
                  {...form.getInputProps("slug_override")}
                />
                <Box>
                  <Text size="sm" fw={700} mb={4}>
                    Corps de l'article
                  </Text>
                  <Suspense fallback={<Loader size="sm" />}>
                    <TiptapEditor
                      value={form.values.body}
                      onChange={(html) => form.setFieldValue("body", html)}
                    />
                  </Suspense>
                </Box>
              </Stack>

              {/* Véhicules mentionnés */}
              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="sm" fw={700}>
                    Véhicules mentionnés
                  </Text>
                  <BrandVehicleSelect
                    availableBrands={available_brands}
                    availableVehicles={available_vehicles}
                    brandIds={form.values.brand_ids}
                    vehicleIds={form.values.vehicle_ids}
                    onBrandsChange={(ids) =>
                      form.setFieldValue("brand_ids", ids)
                    }
                    onVehiclesChange={(ids) =>
                      form.setFieldValue("vehicle_ids", ids)
                    }
                  />
                </Stack>
              </Paper>

              {/* Images */}
              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="sm" fw={700}>
                    Images
                  </Text>

                  <Stack gap="xs">
                    <ImageField
                      label="Image carte"
                      preview={featuredPreview}
                      onDrop={(file) => {
                        form.setFieldValue("featured_image", file);
                        form.setFieldValue("remove_featured_image", false);
                      }}
                      onRemove={() => {
                        form.setFieldValue("featured_image", null);
                        form.setFieldValue("remove_featured_image", true);
                      }}
                    />
                    <Checkbox
                      label="Utiliser aussi comme image de couverture de l'article"
                      checked={form.values.use_card_as_cover}
                      onChange={(e) => {
                        form.setFieldValue(
                          "use_card_as_cover",
                          e.currentTarget.checked,
                        );
                        if (e.currentTarget.checked)
                          form.setFieldValue("cover_image", null);
                      }}
                    />
                  </Stack>

                  {!form.values.use_card_as_cover && (
                    <ImageField
                      label="Image de couverture"
                      preview={coverPreview}
                      onDrop={(file) => {
                        form.setFieldValue("cover_image", file);
                        form.setFieldValue("remove_cover_image", false);
                      }}
                      onRemove={() => {
                        form.setFieldValue("cover_image", null);
                        form.setFieldValue("remove_cover_image", true);
                      }}
                    />
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>

          {/* Rail latéral (~30%) */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="xl">
              {/* Référencement (SEO) */}
              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="sm" fw={700}>
                    Référencement (SEO)
                  </Text>
                  <TextInput
                    label="Meta title"
                    {...form.getInputProps("meta_title")}
                  />
                  <Textarea
                    label="Meta description"
                    autosize
                    minRows={2}
                    maxRows={4}
                    {...form.getInputProps("meta_description")}
                  />
                </Stack>
              </Paper>

              {/* Classification */}
              <Paper p="lg" radius="md" withBorder>
                <Stack gap="md">
                  <Text size="sm" fw={700}>
                    Classification
                  </Text>
                  <TagsInput
                    label="Tags"
                    placeholder="Ajouter un tag…"
                    clearable
                    {...form.getInputProps("tags")}
                  />
                  <Switch
                    label="Publié"
                    checked={form.values.status === "published"}
                    onChange={(e) =>
                      form.setFieldValue(
                        "status",
                        e.currentTarget.checked ? "published" : "draft",
                      )
                    }
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Actions */}
        <Group justify="space-between">
          <Group gap="sm">
            <Button type="submit" color="brand">
              Enregistrer l'article
            </Button>
            {isEdit && (
              <Button
                variant="default"
                leftSection={<IconEye size={16} />}
                onClick={handlePreview}
              >
                Prévisualiser
              </Button>
            )}
          </Group>
          {isEdit && (
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDelete}
            >
              Supprimer l'article
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
};

export default AdminArticlesForm;
